import { useEffect, useState } from 'react';
import { useRoute, useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ArrowLeft, ChevronRight, Clock, Trash2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { adminAvailabilityService } from '@/services/api/admin-availability.service';
import type { GroupClassParticipant } from '@/services/api/admin-availability.service';
import { bookingService } from '@/services/api/booking.service';
import { EditGroupClassSlotDialog } from '@/components/availability/EditGroupClassSlotDialog';
import {
  CancelGroupClassScopeChoice,
  type GroupClassCancelScope,
} from '@/components/availability/CancelGroupClassScopeChoice';
import { EditBatchDialog } from '@/components/bookings/EditBatchDialog';
import { Layers } from 'lucide-react';
import { waitlistService } from '@/services/api/waitlist.service';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/ui/badge';
import type { BookingStatus } from '@/lib/status-colors';
import type { WaitlistEntryAdmin } from '@/types/booking';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';

export default function AdminGroupClassPage() {
  const { t } = useTranslation(['staff', 'common', 'bookings', 'waitlist']);
  const [, params] = useRoute('/admin/group-classes/:id');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const rawId = (params as { id?: string } | null)?.id;
  const slotId = rawId ? Number(rawId) : null;
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<GroupClassCancelScope>('single');
  const [editOpen, setEditOpen] = useState(false);
  const [waitlistRemoveTarget, setWaitlistRemoveTarget] = useState<WaitlistEntryAdmin | null>(null);
  const [peopleTab, setPeopleTab] = useState<'participants' | 'waitlist'>('participants');
  // Per-participant batch actions (only for participants enrolled as a series).
  const [batchEditTarget, setBatchEditTarget] = useState<GroupClassParticipant | null>(null);
  const [batchCancelTarget, setBatchCancelTarget] = useState<GroupClassParticipant | null>(null);
  // Remove a single participant from THIS class only (cancels their one booking).
  const [removeParticipantTarget, setRemoveParticipantTarget] = useState<GroupClassParticipant | null>(null);

  const { data: detail, isLoading, error } = useQuery({
    queryKey: ['admin-group-class-slot', slotId],
    queryFn: () => adminAvailabilityService.getGroupClassSlot(slotId!),
    enabled: !!slotId,
  });

  const waitlistEnabled = detail?.waitlist_enabled === true;

  useEffect(() => {
    if (!waitlistEnabled && peopleTab === 'waitlist') setPeopleTab('participants');
  }, [waitlistEnabled, peopleTab]);
  const { data: waitlistEntries = [], isLoading: loadingWaitlist } = useQuery({
    queryKey: ['admin-waitlist-entries', slotId],
    queryFn: () => waitlistService.adminListBySlot(slotId!),
    enabled: !!slotId && waitlistEnabled,
  });

  // Cancel one participant's whole batch (their future bookings in the series),
  // scoped by email so co-participants are untouched.
  const cancelBatchMutation = useMutation({
    mutationFn: ({ seriesId, customerEmail }: { seriesId: string; customerEmail: string }) =>
      bookingService.cancelSeries(seriesId, customerEmail),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-group-class-slot', slotId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
      setBatchCancelTarget(null);
      toast({
        title: t('common:toasts.success'),
        description: t('bookings:batch.cancelSuccess', {
          defaultValue: '{{count}} bookings cancelled',
          count: result.cancelled_count,
        }),
      });
    },
    onError: (err: any) => {
      toast({
        title: t('common:errors.generic'),
        description: err?.message || t('bookings:batch.cancelError', 'Failed to cancel the batch'),
        variant: 'destructive',
      });
    },
  });

  // Remove one participant from this class = cancel that single booking.
  const removeParticipantMutation = useMutation({
    mutationFn: (bookingId: number) => bookingService.cancelBooking(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-group-class-slot', slotId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
      setRemoveParticipantTarget(null);
      toast({
        title: t('common:toasts.success'),
        description: t('staff:availability.groupClass.participants.removeSuccess', 'Removed from this class.'),
      });
    },
    onError: (err: any) => {
      toast({
        title: t('common:errors.generic'),
        description: err?.message || t('common:errors.generic'),
        variant: 'destructive',
      });
    },
  });

  const removeWaitlistMutation = useMutation({
    mutationFn: (entryId: number) => waitlistService.adminRemove(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-waitlist-entries', slotId] });
      setWaitlistRemoveTarget(null);
      toast({
        title: t('common:toasts.success'),
        description: t('waitlist:admin.removeSuccess', 'Waitlist entry removed.'),
      });
    },
    onError: (err: any) => {
      toast({
        title: t('common:errors.generic'),
        description: err?.message || t('common:errors.generic'),
        variant: 'destructive',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, scope }: { id: number; scope: GroupClassCancelScope }) =>
      adminAvailabilityService.cancelGroupClassSlot(id, scope),
    onSuccess: (result) => {
      toast({
        title: t('common:toasts.success'),
        description: result.cancelled_count > 1
          ? t('staff:availability.groupClass.toasts.cancelledMany', '{{count}} classes cancelled.', {
              count: result.cancelled_count,
            })
          : t('staff:availability.groupClass.toasts.cancelled', 'Group class cancelled.'),
      });
      queryClient.invalidateQueries({ queryKey: ['admin-availability'] });
      queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
      setLocation('/admin/availability');
    },
    onError: (err: any) => {
      toast({
        title: t('common:errors.generic'),
        description: err?.message || t('common:errors.generic'),
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (params: { date: string; start_time: string; provider_id?: number }) =>
      adminAvailabilityService.updateGroupClassSlot(slotId!, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-group-class-slot', slotId] });
      queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
      toast({
        title: t('common:toasts.success'),
        description: t('staff:availability.groupClass.toasts.updated', 'Class updated.'),
      });
      setEditOpen(false);
    },
    onError: (err: any) => {
      toast({
        title: t('common:errors.generic'),
        description: err?.response?.data?.errors?.[0]
          || err?.response?.data?.error
          || err?.message
          || t('staff:availability.errors.failedToUpdate', 'Failed to update class'),
        variant: 'destructive',
      });
    },
  });

  const loadProviderOptions = (params: { date: string; start_time: string }) =>
    slotId
      ? adminAvailabilityService.getGroupClassProviderOptions(slotId, params)
      : Promise.resolve([]);

  const dateLabel = detail
    ? format(parseISO(detail.date), getDateFormats().fullWithWeekday, { locale: getDateLocale() })
    : '';
  const subline = detail ? `${dateLabel} · ${detail.start_time} – ${detail.end_time}` : '';
  useSetPageHeader(
    { title: detail?.service.name ?? '', sub: subline },
    [detail?.service.name, subline],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => setLocation('/admin/availability')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common:back', 'Back')}
        </Button>
        <Card>
          <CardContent className="p-12 text-center text-rose-600">
            {t('staff:availability.groupClass.notFound', 'Group class not found.')}
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasBookings = detail.current_bookings > 0;
  // Only worth offering "and all following" when there are later
  // occurrences of the same recurring class to take with it.
  const isRecurring = !!detail.recurring && (detail.series_remaining ?? 0) > 1;

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLocation('/admin/availability')}
        iconLeft={<ArrowLeft />}
      >
        {t('common:back', 'Back')}
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Card 1 — Appointment summary */}
          <Card>
            <CardHeader>
              <CardTitle>{t('staff:availability.groupClass.tabs.appointment', 'Appointment')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">
                    {t('bookings:details.fields.service', 'Service')}
                  </p>
                  <p className="text-base">{detail.service.name}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">
                    {t('bookings:details.fields.duration', 'Duration')}
                  </p>
                  <p className="text-base">
                    {t('bookings:details.minutesValue', { count: detail.service.duration_minutes })}
                  </p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">
                    {t('bookings:details.fields.date', 'Date')}
                  </p>
                  <p className="text-base">{dateLabel}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">
                    {t('bookings:details.fields.time', 'Time')}
                  </p>
                  <p className="text-base tabular-nums">
                    {detail.start_time} – {detail.end_time}
                  </p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">
                    {t('bookings:details.fields.provider', 'Provider')}
                  </p>
                  <p className="text-base">{detail.provider?.display_name ?? '—'}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">
                    {t('staff:availability.groupClass.tabs.participants', 'Participants')}
                  </p>
                  <p className="text-base tabular-nums inline-flex items-center gap-1.5">
                    <Users className="h-4 w-4 text-violet-500" />
                    {detail.current_bookings}/{detail.max_capacity}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2 — Participants (+ Waitlist tab) */}
          <Card>
            <CardHeader>
              <CardTitle>{t('staff:availability.groupClass.tabs.participants', 'Participants')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={peopleTab} onValueChange={(v) => setPeopleTab(v as typeof peopleTab)} className="space-y-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="participants" className="text-sm">
                    <span className="truncate">
                      {t('staff:availability.groupClass.tabs.participants', 'Participants')}
                    </span>
                    {detail.current_bookings > 0 && (
                      <span className="ml-1.5 shrink-0 inline-flex items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-800 tabular-nums">
                        {detail.current_bookings}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="waitlist" disabled={!waitlistEnabled} className="text-sm">
                    <span className="truncate">
                      {t('staff:availability.groupClass.tabs.waitlist', 'Waitlist')}
                    </span>
                    {waitlistEnabled && waitlistEntries.length > 0 && (
                      <span className="ml-1.5 shrink-0 inline-flex items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-800 tabular-nums">
                        {waitlistEntries.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="participants">
                  {detail.participants.length === 0 ? (
                    <p className="text-center py-8 text-sm text-soft">
                      {t('staff:availability.groupClass.participants.empty', 'No bookings yet for this class.')}
                    </p>
                  ) : (
                    <ul className="divide-y">
                      {detail.participants.map((p) => {
                        const batchCount = p.series_future_count ?? 0;
                        const inBatch = !!p.booking_series_id && batchCount > 0;
                        return (
                        <li key={p.id} className="py-3 flex items-start gap-3">
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="font-medium text-ink break-words">{p.customer_name}</div>
                            <div className="text-xs text-soft break-all">{p.customer_email}</div>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1">
                              <StatusBadge status={p.status as BookingStatus}>
                                {t(`staff:status.${p.status === 'no_show' ? 'noShow' : p.status}`, p.status)}
                              </StatusBadge>
                              {inBatch && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-brand-600">
                                  <Layers className="h-3 w-3" />
                                  {t('bookings:batch.tag', {
                                    defaultValue: 'In a batch ({{count}} upcoming)',
                                    count: batchCount,
                                  })}
                                </span>
                              )}
                              <span className="text-[11px] text-soft">
                                {t('bookings:details.fields.created', 'Created')}{' '}
                                {format(parseISO(p.created_at), `${getDateFormats().medium} HH:mm`, {
                                  locale: getDateLocale(),
                                })}
                              </span>
                            </div>
                            {['pending', 'confirmed'].includes(p.status) && (
                              <div className="flex flex-wrap gap-2 pt-2">
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => setRemoveParticipantTarget(p)}
                                >
                                  {t('staff:availability.groupClass.participants.remove', 'Remove from class')}
                                </Button>
                                {inBatch && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setBatchEditTarget(p)}
                                    >
                                      {t('bookings:batch.edit', {
                                        defaultValue: 'Edit batch ({{count}} slots)',
                                        count: batchCount,
                                      })}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => setBatchCancelTarget(p)}
                                    >
                                      {t('bookings:batch.cancelAll', {
                                        defaultValue: 'Cancel all upcoming ({{count}})',
                                        count: batchCount,
                                      })}
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setLocation(`/admin/bookings/${p.public_id}`)}
                            className="shrink-0 h-9 w-9 p-0"
                            aria-label={t('common:actions.view', 'View')}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </TabsContent>

                {waitlistEnabled && (
                  <TabsContent value="waitlist">
                    {loadingWaitlist ? (
                      <p className="text-center py-8 text-sm text-soft">{t('common:loading', 'Loading...')}</p>
                    ) : waitlistEntries.length === 0 ? (
                      <div className="text-center py-8 text-sm text-soft">
                        <Clock className="h-8 w-8 mx-auto mb-2 text-violet-300" />
                        {t('waitlist:admin.empty', 'No one is on the waitlist yet.')}
                      </div>
                    ) : (
                      <ul className="divide-y">
                        {waitlistEntries.map((entry, idx) => (
                          <li key={entry.id} className="py-3 flex items-start gap-3">
                            <span className="inline-flex h-7 w-7 mt-0.5 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold text-violet-800 tabular-nums">
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="font-medium text-ink break-words">{entry.customer_full_name}</div>
                              <div className="text-xs text-soft break-all">{entry.customer_email}</div>
                              <div className="text-[11px] text-soft pt-0.5">
                                {t('waitlist:admin.joinedAt', 'Joined')}{' '}
                                {format(parseISO(entry.created_at), `${getDateFormats().medium} HH:mm`, {
                                  locale: getDateLocale(),
                                })}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setWaitlistRemoveTarget(entry)}
                              className="shrink-0 h-9 w-9 p-0 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                              aria-label={t('waitlist:admin.removeButton', 'Remove')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('bookings:details.actionsTitle', 'Actions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setEditOpen(true)}
              >
                {t('common:actions.edit', 'Edit')}
              </Button>
              <Button
                variant="destructive"
                className="w-full"
                disabled={cancelMutation.isPending}
                onClick={() => {
                  // Confirm whenever there is something to weigh up: people to
                  // email, or a choice between this class and the rest of the batch.
                  if (hasBookings || isRecurring) {
                    setCancelScope('single');
                    setCancelDialogOpen(true);
                  } else {
                    cancelMutation.mutate({ id: detail.id, scope: 'single' });
                  }
                }}
              >
                {cancelMutation.isPending
                  ? t('common:actions.cancelling', 'Cancelling...')
                  : t('staff:availability.groupClass.actions.cancelClass', 'Cancel class')}
              </Button>
              {hasBookings && (
                <p className="text-xs text-soft">
                  {t(
                    'staff:availability.groupClass.cancelNotifyHint',
                    'All {{count}} participant(s) will be emailed.',
                    { count: detail.current_bookings }
                  )}
                </p>
              )}
            </CardContent>
          </Card>

          <AlertDialog
            open={waitlistRemoveTarget !== null}
            onOpenChange={(open) => { if (!open && !removeWaitlistMutation.isPending) setWaitlistRemoveTarget(null); }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('waitlist:admin.removeConfirmTitle', 'Remove from waitlist?')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    'waitlist:admin.removeConfirmDescription',
                    '{{name}} will be emailed that they were removed. This cannot be undone.',
                    { name: waitlistRemoveTarget?.customer_full_name ?? '' },
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={removeWaitlistMutation.isPending}>
                  {t('common:actions.cancel', 'Cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-rose-600 hover:bg-rose-700"
                  disabled={removeWaitlistMutation.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    if (waitlistRemoveTarget) removeWaitlistMutation.mutate(waitlistRemoveTarget.id);
                  }}
                >
                  {t('waitlist:admin.removeButton', 'Remove')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    'staff:availability.groupClass.cancelConfirm.title',
                    'Cancel this group class?'
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {detail.current_bookings > 0
                    ? t(
                        'staff:availability.groupClass.cancelConfirm.description',
                        '{{count}} participant(s) will be notified by email. This cannot be undone.',
                        { count: detail.current_bookings }
                      )
                    : t(
                        'staff:availability.groupClass.cancelConfirm.descriptionEmpty',
                        'This cannot be undone.'
                      )}
                </AlertDialogDescription>
              </AlertDialogHeader>

              {detail.recurring && (detail.series_remaining ?? 0) > 1 && (
                <CancelGroupClassScopeChoice
                  value={cancelScope}
                  onChange={setCancelScope}
                  seriesRemaining={detail.series_remaining ?? 0}
                />
              )}

              <AlertDialogFooter>
                <AlertDialogCancel>
                  {t('staff:availability.groupClass.cancelConfirm.keep', 'Keep class')}
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-danger text-white hover:bg-danger/90"
                  onClick={() => {
                    setCancelDialogOpen(false);
                    cancelMutation.mutate({ id: detail.id, scope: cancelScope });
                  }}
                >
                  {t(
                    'staff:availability.groupClass.cancelConfirm.confirm',
                    'Yes, cancel and notify'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <EditGroupClassSlotDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        detail={detail}
        onSave={async (params) => { await updateMutation.mutateAsync(params); }}
        onCancelClass={async (scope) => { await cancelMutation.mutateAsync({ id: detail.id, scope }); }}
        loadProviderOptions={loadProviderOptions}
        isSaving={updateMutation.isPending}
        isCancelling={cancelMutation.isPending}
        onViewParticipant={(publicId) => setLocation(`/admin/bookings/${publicId}`)}
        waitlistEntries={waitlistEntries}
        isLoadingWaitlist={loadingWaitlist}
        onRemoveWaitlistEntry={async (entryId) => { await removeWaitlistMutation.mutateAsync(entryId); }}
      />

      {/* Per-participant batch edit */}
      {batchEditTarget?.booking_series_id && (
        <EditBatchDialog
          seriesId={batchEditTarget.booking_series_id}
          customerEmail={batchEditTarget.customer_email}
          count={batchEditTarget.series_future_count ?? 0}
          open={!!batchEditTarget}
          onOpenChange={(open) => { if (!open) setBatchEditTarget(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-group-class-slot', slotId] });
            queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
          }}
        />
      )}

      {/* Remove a single participant from this class (single-booking cancel) */}
      <AlertDialog open={!!removeParticipantTarget} onOpenChange={(open) => { if (!open && !removeParticipantMutation.isPending) setRemoveParticipantTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('staff:availability.groupClass.participants.removeTitle', {
                defaultValue: 'Remove {{name}} from this class?',
                name: removeParticipantTarget?.customer_name ?? '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'staff:availability.groupClass.participants.removeDescription',
                "Cancels their booking for this class only. Any other bookings they have are untouched. They'll be emailed.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeParticipantMutation.isPending}>
              {t('common:actions.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-danger text-white hover:bg-danger/90"
              disabled={removeParticipantMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (removeParticipantTarget) removeParticipantMutation.mutate(removeParticipantTarget.id);
              }}
            >
              {t('staff:availability.groupClass.participants.removeConfirm', 'Remove from class')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Per-participant batch cancel confirmation */}
      <AlertDialog open={!!batchCancelTarget} onOpenChange={(open) => { if (!open) setBatchCancelTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('bookings:batch.cancelTitle', 'Cancel this participant\'s batch?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('bookings:batch.cancelConfirm', {
                defaultValue: 'Cancel all {{count}} upcoming bookings for this customer in the batch?',
                count: batchCancelTarget?.series_future_count ?? 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-danger text-white hover:bg-danger/90"
              disabled={cancelBatchMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (batchCancelTarget?.booking_series_id) {
                  cancelBatchMutation.mutate({
                    seriesId: batchCancelTarget.booking_series_id,
                    customerEmail: batchCancelTarget.customer_email,
                  });
                }
              }}
            >
              {t('bookings:batch.cancelAllShort', 'Cancel batch')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
